/**
 * bridge/document_scanner_controller.js — "document-scanner" bridge component.
 *
 * Capability-tier bridge component: opens a native document scanner (edge
 * detection + perspective correction) and hands the captured page back to the web
 * layer as a base64 JPEG, which Chippie turns into a Blob to preview/upload for
 * receipt capture. Stimulus identifier "bridge--document-scanner" (glob-derived
 * from this subdir); Strada component name "document-scanner" MUST match the iOS
 * class var name (native/ios/Chippie/Bridge/DocumentScannerComponent.swift).
 *
 * Why it extends Strada's BridgeComponent: the iOS scanner is reached via
 * this.send("scan", {}, callback), which only works on a connected bridge
 * component inside the iOS Hotwire Native shell. Extending BridgeComponent gives
 * us that send() transport AND the UA-gated connect() (it only connects in a
 * native shell). On connect it contributes a single documentScanner() impl set to
 * the window.ChippieBridge facade ({ ios, android, pwa }); the facade owns
 * platform detection and picks the right impl per call.
 *
 * The facade method is async-by-callback: callers pass a callback that receives
 * { imageData, pageCount } where imageData is a base64 JPEG string (no data:
 * prefix) or null when the user cancelled.
 *   - ios:     this.send("scan", {}, msg => cb(msg.data)) over Strada to
 *              DocumentScannerComponent.swift.
 *   - android: window.ChippieNative.scanDocument(token) — fire-and-forget; the
 *              Kotlin side launches ML Kit's GmsDocumentScanner and resolves the
 *              result by calling window.__chippieScanResolve(token, json) via
 *              evaluateJavascript. We register that resolver here so the facade
 *              can correlate the async reply to the right callback.
 *   - pwa:     no native scanner; trigger a hidden <input capture> file picker so
 *              the user can still snap a receipt with the device camera. The result
 *              is a base64 JPEG (read via FileReader) — same shape as native, just
 *              without edge detection.
 *
 * Template wiring:
 *   <div data-controller="bridge--document-scanner">
 *     <button data-action="bridge--document-scanner#scan">Scan receipt</button>
 *     <div data-bridge--document-scanner-target="result"></div>
 *   </div>
 * Or call window.ChippieBridge.documentScanner(result => { ... }) directly.
 *
 * Web side adapted from joemasilotti/bridge-components document_scanner_controller.js
 * (base64 -> Blob preview); the facade registration + Android/PWA fallbacks are
 * Chippie's three-way transport layer.
 */

import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Monotonic token generator + pending-callback map for the Android round trip,
// which returns asynchronously through window.__chippieScanResolve. Module-scoped
// so it survives whether or not the controller is connected.
let scanToken = 0;
const pendingScans = new Map();

function installAndroidResolver() {
  if (window.__chippieScanResolve) return;
  // Called by ChippieNativeBridge.kt via evaluateJavascript once ML Kit returns.
  // json is a string: { imageData: <base64|null>, pageCount: <int> }.
  window.__chippieScanResolve = (token, json) => {
    const cb = pendingScans.get(token);
    if (!cb) return;
    pendingScans.delete(token);
    let data = { imageData: null, pageCount: 0 };
    try { data = JSON.parse(json); } catch (_e) { /* malformed -> treat as cancel */ }
    cb(data);
  };
}

// PWA fallback: prompt a camera capture and read the file as a base64 JPEG so the
// callback gets the same { imageData, pageCount } shape as the native scanners.
function pwaScan(callback) {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.capture = "environment";
  input.style.display = "none";
  input.addEventListener("change", () => {
    const file = input.files && input.files[0];
    document.body.removeChild(input);
    if (!file) {
      callback({ imageData: null, pageCount: 0 });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      // Strip the "data:image/...;base64," prefix to match the native payload.
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      callback({ imageData: base64 || null, pageCount: base64 ? 1 : 0 });
    };
    reader.onerror = () => callback({ imageData: null, pageCount: 0 });
    reader.readAsDataURL(file);
  });
  document.body.appendChild(input);
  input.click();
}

function registerStandalone() {
  if (!window.ChippieBridge || !window.ChippieBridge.register) return;
  installAndroidResolver();
  window.ChippieBridge.register("documentScanner", {
    // ios is overridden by connect() with a real send; placeholder no-op (with a
    // cancel reply) keeps callers safe if selected before the shell connects.
    ios: (callback) => { if (typeof callback === "function") callback({ imageData: null, pageCount: 0 }); },
    android: (callback) => {
      if (typeof callback !== "function") return;
      if (!(window.ChippieNative && window.ChippieNative.scanDocument)) {
        callback({ imageData: null, pageCount: 0 });
        return;
      }
      const token = ++scanToken;
      pendingScans.set(token, callback);
      try {
        window.ChippieNative.scanDocument(String(token));
      } catch (_e) {
        pendingScans.delete(token);
        callback({ imageData: null, pageCount: 0 });
      }
    },
    pwa: (callback) => { if (typeof callback === "function") pwaScan(callback); },
  });
}

// Facade may load after this module; register as soon as it is present.
if (window.ChippieBridge) {
  registerStandalone();
} else {
  document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
  static component = "document-scanner";
  static targets = ["result"];

  connect() {
    super.connect();
    // Inside the iOS shell now: wire the real Strada send into the facade, keeping
    // the Android/PWA fallbacks intact.
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    installAndroidResolver();
    window.ChippieBridge.register("documentScanner", {
      ios: (callback) => {
        this.send("scan", {}, (message) => {
          if (typeof callback === "function") {
            callback((message && message.data) || { imageData: null, pageCount: 0 });
          }
        });
      },
      android: (callback) => {
        if (typeof callback !== "function") return;
        if (!(window.ChippieNative && window.ChippieNative.scanDocument)) {
          callback({ imageData: null, pageCount: 0 });
          return;
        }
        const token = ++scanToken;
        pendingScans.set(token, callback);
        try {
          window.ChippieNative.scanDocument(String(token));
        } catch (_e) {
          pendingScans.delete(token);
          callback({ imageData: null, pageCount: 0 });
        }
      },
      pwa: (callback) => { if (typeof callback === "function") pwaScan(callback); },
    });
  }

  disconnect() {
    // Leaving the iOS shell: drop the live-send impl back to the standalone
    // placeholder so a stale this.send is never invoked.
    registerStandalone();
    super.disconnect();
  }

  // data-action="bridge--document-scanner#scan" trigger. Scans, then previews the
  // first page in the result target (if present) as a Blob URL — base64 over the
  // bridge is decoded to a Blob to keep a large data: URL off the DOM.
  scan(event) {
    if (event) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
    if (this.hasResultTarget) this.resultTarget.innerHTML = "";

    window.ChippieBridge.documentScanner((data) => {
      const imageData = data && data.imageData;
      if (!imageData) return; // cancelled or failed
      if (!this.hasResultTarget) return;

      const blob = this.#base64ToBlob(imageData.trim(), "image/jpeg");
      const blobUrl = URL.createObjectURL(blob);
      const img = document.createElement("img");
      img.src = blobUrl;
      img.onload = () => URL.revokeObjectURL(blobUrl);
      this.resultTarget.appendChild(img);
    });
  }

  // Base64 JPEG -> Blob (smaller in the DOM than a data: URL).
  #base64ToBlob(base64, mimeType) {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  }
}
